import { Component, output, OnInit, DestroyRef, inject } from '@angular/core';
import { ApplicantView } from './applicant-view';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { EditTaxPartnerDialogComponent } from '@app/applications/edit-tax-partner-dialog/edit-tax-partner-dialog.component';
import { StingrayAuthenticationService } from '@app/core/authentication/stingray-authentication.service';
import { Customer, Financing, Organization, Shareholder, Person, Company, TaxPartner, Collaboration, NewCollaboration, Member, BuildingOwnerPerson, NewCompany } from '@app/core/stingray-api/models';
import { BuildingInfoService, CollaborationsService, CompaniesService, CompanyInfoService, CustomersService, PersonsService, ProspectsService, ReportsService } from '@app/core/stingray-api/services';
import { ToasterService } from '@app/core/toaster/toaster.service';
import { getFormGroupValue, convertFormGroupToPatchOperations } from '@app/validators/form-group-tools';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, forkJoin, finalize, Observable } from 'rxjs';
import { createAddressFormGroup } from '../address-input-v2/address-input-v2-tools';
import { ApplicantSearchResult } from './applicant-search-result';
import { CompanyApplicantView } from './company-applicant-view';
import { NewApplicantView } from './new-applicant-view';
import { PersonApplicantView } from './person-applicant';
import { ShareholderView } from './shareholder-view';
import { CustomerAutocompleteComponent } from '../customer-autocomplete/customer-autocomplete.component';
import { MatButton } from '@angular/material/button';
import { ListItemSelectComponent } from '../list/list-item-select/list-item-select.component';
import { MaritalStatusInputComponent } from '../maritalstatus-input/maritalstatus-input.component';
import { NationalityAutocompleteComponent } from '../nationality-autocomplete/nationality-autocomplete.component';
import { MatSelect } from '@angular/material/select';
import { ListItemInputComponent } from '../list/list-item-input/list-item-input.component';
import { MatList, MatListItem } from '@angular/material/list';
import { MatCheckbox } from '@angular/material/checkbox';
import { AddressInputV2Component } from '../address-input-v2/address-input-v2.component';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelActionRow } from '@angular/material/expansion';
import { LoadingButtonComponent } from '../loading-button/loading-button.component';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/core';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgClass } from '@angular/common';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatCard, MatCardContent } from '@angular/material/card';

@Component({
    selector: 'unite-search-create-applicants',
    templateUrl: './search-create-applicants.component.html',
    styleUrls: ['./search-create-applicants.component.scss'],
    imports: [MatCard, MatCardContent, MatFormField, MatLabel, TranslateModule, MatProgressSpinner, MatSuffix, MatInput, ReactiveFormsModule, MatAutocompleteTrigger, MatAutocomplete, MatOption, MatIcon, LoadingButtonComponent, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, NgClass, MatRadioGroup, MatRadioButton, AddressInputV2Component, MatCheckbox, MatList, MatListItem, ListItemInputComponent, MatSelect, NationalityAutocompleteComponent, MaritalStatusInputComponent, ListItemSelectComponent, MatExpansionPanelActionRow, MatButton, CustomerAutocompleteComponent]
})
export class SearchCreateApplicantsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  customerChange = output<{ customer: Customer; proceedToCalculation: boolean }>();

  applicants: ApplicantView[] = [];
  applicantSearchGroup: UntypedFormGroup;
  applicantSearchResults: ApplicantSearchResult[] = [];

  customer: Customer;

  isAddingApplicant = false;
  isSearchingApplicants = false;

  constructor(
    private buildingInfoService: BuildingInfoService,
    private collaborationService: CollaborationsService,
    private companiesService: CompaniesService,
    private companyInfoService: CompanyInfoService,
    private customersService: CustomersService,
    private formBuilder: UntypedFormBuilder,
    private matDialog: MatDialog,
    private personsService: PersonsService,
    private prospectsService: ProspectsService,
    private toasterService: ToasterService,
    private translateService: TranslateService) { }

  ngOnInit(): void {

    this.applicantSearchGroup = this.formBuilder.group({ customer: [] });

    this.applicantSearchGroup.get('customer').valueChanges
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value) {

          // Start a search
          if (typeof value === 'string' && value.trim() !== '') {

            this.isSearchingApplicants = true;

            const customerSearch = this.customersService.getCustomers({ name: value, count: 3, order: 'Desc', sort: 'Created' });
            const companyInfoSearch = this.companyInfoService.findCompanies({ tradeName: value, count: 7 });

            forkJoin([companyInfoSearch, customerSearch])
              .pipe(finalize(() => this.isSearchingApplicants = false), takeUntilDestroyed(this.destroyRef))
              .subscribe(([companySearchResults, customers]) => {
                this.applicantSearchResults = [];

                this.applicantSearchResults.push(...customers.items.map(x => <ApplicantSearchResult>{
                  name: x.name,
                  customerSearchResult: x
                }));

                this.applicantSearchResults.push(...companySearchResults.map(x => <ApplicantSearchResult>{
                  name: `${x.name}` + (x.city ? ` (${x.city})` : ''),
                  companySearchResult: x
                }));

              });
          } else {
            // Auto add predefined types
            this.addApplicant();
          }
        }
      });
  }

  displayApplicantSearchResult(result: ApplicantSearchResult): string {
    return result?.name;
  }

  canAddApplicant(): boolean {
    return !!this.applicantSearchGroup.get('customer').value;
  }

  /**
   * Transforms a new shareholder into a named shareholder
   */
  nameShareholder(shareholderView: ShareholderView, shareholder: Shareholder, isContact: boolean): void {
    const value = shareholderView.search.get('customer').value;

    shareholderView.shareholderForm = this.formBuilder.group({
      share: [shareholder ? shareholder.share : null, [Validators.required]],
      isContact: [isContact, []]
    });

    // New shareholder
    if (typeof value === 'string' && value.trim() !== '') {
      shareholderView.name = value;
      shareholderView.form = this.createShareholderFormGroup(value, null);
    } else {
      // shareholderView.isProcessing = true;
      shareholderView.name = value.name;
      this.personsService
        .getPersonById({ id: value.id })
        // .pipe(finalize(() => shareholderView.isProcessing = false))
        .subscribe(person => {
          shareholderView.person = person;
          shareholderView.form = this.createShareholderFormGroup(null, person);
          shareholderView.isProcessed = shareholderView.isValid();
        });
    }
  }

  hasTaxPartner(view: PersonApplicantView | ShareholderView): boolean {
    if (!view.form) {
      return false;
    }

    const status = view.form.get('maritalStatus').value;
    return status && (status.indexOf('Registered') > -1 || status.indexOf('Married') > -1);
  }

  /**
   * Persists the shareholder by saving the person and the shareholder details
   */
  saveShareholder(company: CompanyApplicantView, shareholder: ShareholderView): void {
    shareholder.form.markAllAsTouched();

    if (shareholder.isValid()) {
      shareholder.isProcessing = true;
      this.doSavePerson(shareholder).subscribe(() => {



        this.companiesService.addCompanyShareholders({
          id: company.company.id, body: [
            {
              customerId: shareholder.person.id,
              customerType: 'Person',
              email: shareholder.person.email,
              share: shareholder.shareholderForm.get('share').value
            }
          ]
        })
          // .pipe(finalize(() => shareholder.isProcessing = false))
          .subscribe(() => {
            shareholder.isProcessing = true;

            if (shareholder.shareholderForm.get('isContact').value) {
              this.companiesService.patchCompany({
                id: company.company.id,
                body: [
                  { op: 'replace', path: 'contact', value: { id: shareholder.person.id, name: shareholder.person.name } }
                ]
              }).subscribe(() => {
                this.loadApplicantAsCompany(company.company.id).subscribe(() => {
                  shareholder.isProcessed = true;
                });
              });
            } else {
              this.loadApplicantAsCompany(company.company.id).subscribe(() => {
                shareholder.isProcessed = true;
              });
            }
          });
      });
    }
  }
  /**
   * Loads a applicant panel for a panel
   * @param person the data to prefill
   */
  addApplicantPersonView(name: string, person: Person): void {
    const existing = this.applicants.filter(x => person && x.customerId === person.id);
    const isNew = existing.length === 0;
    const view = isNew ? new PersonApplicantView() : existing[0] as PersonApplicantView;

    view.person = person;
    view.customerId = person?.id;
    view.name = name;
    view.form = this.createPersonFormGroup(name, person);

    view.isTaxPartnerApplicantControl = this.createIsTaxPartnerApplicantFormControl(view);


    view.form.get('surname').valueChanges.subscribe(() => {

      const initials = view.form.get('initials').value;
      const middleName = view.form.get('middleName').value;
      const surname = view.form.get('surname').value;

      if (initials && surname) {
        view.name = `${initials} ${middleName ?? ''} ${surname}`;
      }
    });

    if (isNew) {
      this.applicants.push(view);
    }
  }

  /**
   * Loads a applicant panel for a company
   * @param company the data to prefill
   * @param shareholders the data to prefill
   */
  addApplicantCompanyView(name: string, company: Company, shareholders: Shareholder[]): void {


    const existing = this.applicants.filter(x => company && x.customerId === company.id);
    const isNew = existing.length === 0;
    const view = isNew ? new CompanyApplicantView() : existing[0] as CompanyApplicantView;
    view.form = this.createApplicantCompanyForm(name, company);

    view.name = name;
    view.customerId = company?.id;
    view.company = company;


    view.shareholders = shareholders ?? [];

    if (shareholders) {
      view.shareholderViews = [];
      shareholders.forEach(shareholder => {
        if (shareholder.customerType === 'Person') {
          this.customersService
            .getCustomerById({ id: shareholder.customerId })
            .subscribe(customer => {
              this.addApplicantShareholderView(view, customer, shareholder);
            });
        }
      });
    }

    if (isNew) {
      this.applicants.push(view);
    }
  }

  showExtendedInvestmentProfile(person: Person): boolean {
    return true;
  }


  /**
   * Adds a new applicant based on the entered OR selected customer value in the search box
   */
  addApplicant(): void {

    const value = this.applicantSearchGroup.get('customer').value;

    if (value) {

      // New applicant
      if (typeof value === 'string' && value.trim() !== '') {

        const view = new NewApplicantView();
        view.name = value;
        view.form = this.createApplicantNewForm();
        view.type = 'New';
        this.applicants.push(view);

      } else {

        this.isAddingApplicant = true;
        const applicantSearchResult = <ApplicantSearchResult>value;

        // Customer search result
        if (applicantSearchResult.customerSearchResult) {

          this.loadApplicant(applicantSearchResult.customerSearchResult)
            .subscribe(() => this.isAddingApplicant = false);
        } else {
          // Company info search result
          this.companiesService.addCompanyByChamberOfCommerceNumber({ chamberOfCommerceNumber: applicantSearchResult.companySearchResult.chamberOfCommerceNumber })
            .pipe(finalize(() => this.isAddingApplicant = false), takeUntilDestroyed(this.destroyRef))
            .subscribe(company => {
              this.loadApplicantAsCompany(company.id).subscribe(() => { });
            });
        }
      }
    }
  }

  public loadApplicant(customer: Customer): Observable<boolean> {
    // Fetch the current customer
    if (customer.type === 'Company') {
      return this.loadApplicantAsCompany(customer.id);
    } else if (customer.type === 'Person') {
       return this.loadApplicantAsPerson(customer.id);
    } else if (customer.type === 'Collaboration') {
      return this.loadApplicantAsCollaboration(customer.id);
    }
  }

  /**
   * Fetches the owners of an adress to show as possible applicants
   */
  retrievePossibleApplicants(applicantView: NewApplicantView): void {

    applicantView.isRetrievingBuildingOwners = true;

    this.buildingInfoService
      .getBuildingOwnerInfo({
        postalCode: applicantView.form.get('address.postalCode').value,
        houseNumber: applicantView.form.get('address.houseNumber').value,
        houseNumberAddition: applicantView.form.get('address.houseNumberAddition').value,
        includeAddressRegistrations: false,
        createCustomerIfNotExisting: false
      })
      .pipe(finalize(() => applicantView.isRetrievingBuildingOwners = false), takeUntilDestroyed(this.destroyRef))
      .subscribe(buildingOwners => {
        applicantView.buildingOwners = buildingOwners;
      });
  }

  showValidationError(control: AbstractControl): boolean {
    return control.touched && control.invalid;
  }

  isTaxPartnerRequired(view: PersonApplicantView | ShareholderView): boolean {
    return view.form.get('taxPartner.name').hasValidator(Validators.required);
  }

  /**
   * Opens edit tax partner dialog
   */
  editTaxPartner(applicant: PersonApplicantView): void {
    const dialogConfig = this.createDialogConfig();
    const taxPartner = applicant.form.get('taxPartner').value as TaxPartner;
    dialogConfig.data = taxPartner.id ? taxPartner : null;

    this.matDialog
      .open(EditTaxPartnerDialogComponent, dialogConfig)
      .afterClosed()
      .subscribe((taxPartnerPatch) => {
        applicant.form.get('taxPartner').patchValue(taxPartnerPatch);
      });
  }

  /**
   * Saves the company and adds a shareholder view
   */
  saveCompanyAndAddApplicantShareholderView(applicant: CompanyApplicantView): void {
    if (applicant.form.valid) {
      this.doSaveCompany(applicant)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.addApplicantShareholderView(applicant, null, null);
        });
    } else {
      this.toasterService.info(this.translateService.instant('Please enter company information before adding shareholders'));
    }
  }

  /**
   * Removes applicant view
   */
  removeApplicant(applicant: ApplicantView): void {
    this.applicants.splice(this.applicants.indexOf(applicant), 1);
  }

  /**
   * Adds or updates the applicant based on the view
   */
  saveApplicant(applicant: ApplicantView): void {

    if (!applicant.isValid()) {
      return;
    }

    applicant.isProcessing = true;


    if (applicant.type === 'New') {

      const view = applicant as NewApplicantView;
      const customerType = view.form.get('customerType').value;

      if (customerType === 'Company') {
        this.changeApplicantType(applicant, 'Company');
      } else if (customerType === 'Person') {

        if (view.selectedBuildingOwnerPersons.length === 0) {
          this.changeApplicantType(applicant, 'Person');
        } else {

          const addPersonRequests = view.selectedBuildingOwnerPersons.map(p => this.getOrCreatePersonBasedBuildingOwnerPerson(p));

          forkJoin(addPersonRequests)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(createdPersonIds => {

              const loadCustomerPersons = createdPersonIds.map(createdPersonId => this.loadApplicantAsPerson(createdPersonId));

              forkJoin(loadCustomerPersons)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                  // Remove this panel, there will be new ones
                  this.removeApplicant(applicant);
                });

            });
        }
      }

    } else if (applicant.type === 'Company') {

      // A company
      this.doSaveCompany(applicant as CompanyApplicantView)
        .pipe(finalize(() => applicant.isProcessing = false), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          applicant.isProcessing = false;
          applicant.isProcessed = true;
        });

    } else if (applicant.type === 'Person') {

      // A person
      this.doSavePerson(applicant as PersonApplicantView)
        .pipe(finalize(() => applicant.isProcessing = false), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          console.log(`http://localhost:4200/customers/${applicant.customerId}/details`);
          applicant.isProcessing = false;
          applicant.isProcessed = true;
        });
    }
  }


  /**
   * Removes the shareholder from the company, persists data if it was already saved in the database
   */
  removeShareholder(company: CompanyApplicantView, shareholder: ShareholderView): void {
    const idx = company.shareholderViews.indexOf(shareholder);

    if (shareholder.shareholderId) {
      shareholder.isProcessing = true;

      this.companiesService
        .deleteCompanyShareholderById({ id: company.company.id, shareholderId: shareholder.shareholderId })
        .pipe(finalize(() => shareholder.isProcessing = false))
        .subscribe(() => {
          company.shareholderViews.splice(idx);
          this.loadApplicantAsCompany(company.company.id);
        });
    } else {
      company.shareholderViews.splice(idx);
    }
  }

  /**
   * Checks if the currently setup data is enough to continue
   * @returns true if so, false if not
   */
  public canSaveApplication(): boolean {
    return this.applicants.length > 0 &&  // Has applicants
      this.applicants.filter(b => !b.customerId).length === 0 && // All have been saved
      this.applicants.filter(b => !b.isValid()).length === 0 && // All are valid
      this.applicants.filter(b => !b.isProcessed).length === 0; // All have been processed

  }

  public loadApplicantAsPerson(personId: string): Observable<boolean> {
    return new Observable<boolean>(obj => {

      this.personsService
        .getPersonById({ id: personId })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(person => {
          obj.next(true);
          obj.complete();
          this.addApplicantPersonView(person.name, person);
        });
    });
  }

  public loadApplicantAsCompany(companyId: string): Observable<boolean> {

    return new Observable<boolean>(obj => {

      this.companiesService
        .getCompanyById({ id: companyId })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(company => {

          // Fetch the shareholders
          this.companiesService.getCompanyShareholdersById({ id: company.id })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(shareholders => {


              this.addApplicantCompanyView(company.name, company, shareholders);

              obj.next(true);
              obj.complete();
            });
        });
    });
  }

  public loadApplicantAsCollaboration(collaborationId: string): Observable<boolean> {
    return new Observable<boolean>(obj => {
      this.collaborationService
        .getCollaborationById({ id: collaborationId })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(collaboration => {

          // Fetch the members of collaboration
          this.collaborationService.getCollaborationMembers({ id: collaboration.id })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(members => {

              const requests = members.map(x => x.type === 'Person' ? this.loadApplicantAsPerson(x.customerId) : this.loadApplicantAsCompany(x.customerId));
              forkJoin(requests)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                  obj.next(true);
                  obj.complete();
                });
            });
        });
    });
  }

  public saveApplicants(): Observable<string> {
    return new Observable<string>(obj => {
      if (this.applicants.length > 1) {
        // We save the collaboration
        this.saveCollaboration()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(collaboration => {
            this.promoteToCustomer(collaboration.id)
              .subscribe(() => {
                //            this.validatePreferredProposalAndCreateApplication();
                obj.next(collaboration.id);
                obj.complete();
              });
          });
      } else {
        this.promoteToCustomer(this.applicants[0].customerId)
          .subscribe(() => {
            //      this.validatePreferredProposalAndCreateApplication();
            obj.next(this.applicants[0].customerId);
            obj.complete();
          });
      }
    });
  }

  /**
   * Promotes a prospect to the customer and adds the application
   */
  private promoteToCustomer(targetCustomerId: string): Observable<boolean> {

    return new Observable<boolean>(obj => {
      console.log(this.customer);
      console.log(targetCustomerId);

      if (this.customer.id !== targetCustomerId && this.customer.type === 'Prospect') {
        this.prospectsService
          .promoteProspectToCustomer({ id: this.customer.id, customerId: targetCustomerId })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(x => {
            this.loadCustomerById(x.id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(() => {
                // this.validatePreferredProposalAndCreateApplication();
                obj.next(true);
                obj.complete();
              });
          });
      } else {
        // this.validatePreferredProposalAndCreateApplication();
        obj.next(true);
        obj.complete();
      }
    });
  }

  private loadCustomerById(id: string): Observable<boolean> {
    return new Observable<boolean>(obj => {
      this.customersService.getCustomerById({ id: id })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(x => {
          this.customer = x;
          this.customerChange.emit({ customer: x, proceedToCalculation: false });
          obj.next(true);
          obj.complete();
        });
    });
  }

  /**
   *  Save applicants as collaboration
   */
  private saveCollaboration(): Observable<Collaboration> {
    return new Observable((obj) => {

      const newCollaboration = {} as NewCollaboration;

      const firstApplicant = this.applicants[0];


      newCollaboration.members = this.applicants.map(x => {
        const personView = x as ApplicantView;
        return {
          customerId: personView.customerId
        } as Member;

      });



      newCollaboration.address = firstApplicant instanceof PersonApplicantView ? firstApplicant.person.address : (firstApplicant as CompanyApplicantView).company.address;
      newCollaboration.email = firstApplicant instanceof PersonApplicantView ? firstApplicant.person.email : (firstApplicant as CompanyApplicantView).company.email;
      newCollaboration.phone = firstApplicant instanceof PersonApplicantView ? firstApplicant.person.phone : (firstApplicant as CompanyApplicantView).company.phone;
      newCollaboration.skipIfExists = true; // Upserts the collaboration

      this.collaborationService
        .addCollaboration({ body: newCollaboration })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(createdCollaboration => {
          obj.next(createdCollaboration);
          obj.complete();
        });


    });
  }

  /**
   * Gets or creates a person based on the found building owner
   */
  private getOrCreatePersonBasedBuildingOwnerPerson(p: BuildingOwnerPerson): Observable<string> {
    return new Observable<string>(obj => {

      this.personsService.getPersonById({ id: p.cadastralId }).subscribe(person => {

        if (person) {
          obj.next(person.id);
          obj.complete();
        } else {
          this.personsService.addPerson({
            body: {
              firstName: p.firstName,
              middleName: p.middleName,
              surname: p.surname,
              cadastralId: p.cadastralId,
              address: p.address,
              birthdate: p.birthdate,
              gender: p.gender,
              givenNames: p.givenNames,
              initials: p.initials,
              maritalStatus: p.maritalStatus,
            }
          }).subscribe(personGuid => {
            obj.next(personGuid.id);
            obj.complete();
          });
        }
      });
    });
  }

  /**
   * Changes the applicant type of a new applicant to the desired type (can either be a person or a company)
   */
  private changeApplicantType(applicantView: ApplicantView, newType: 'Company' | 'Person'): void {
    const idx = this.applicants.indexOf(applicantView);
    this.applicants.splice(idx);

    if (newType === 'Company') {
      this.addApplicantCompanyView(applicantView.name, null, null);
    } else if (newType === 'Person') {
      this.addApplicantPersonView(applicantView.name, null);
    }
  }

  private doSaveCompany(applicant: CompanyApplicantView): Observable<boolean> {

    return new Observable<boolean>((obj) => {

      if (applicant.company == null) {
        const newCompany = {
          type: 'Company',
          name: applicant.form.get('name').value,
          legalForm: applicant.form.get('legalForm').value,
          address: applicant.form.get('address').value,
          chamberOfCommerceNumber: applicant.form.get('chamberOfCommerceNumber').value,
          contact: getFormGroupValue(applicant.form.get('contact') as UntypedFormGroup)
        } as NewCompany;

        this.companiesService
          .addCompany({ body: newCompany })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(createdCompany => {

            applicant.customerId = createdCompany.id;
            applicant.company = createdCompany;

            obj.next(true);
            obj.complete();

          });

      } else {

        // Some magic to build patch operations
        const operations = convertFormGroupToPatchOperations(applicant.form);

        this.companiesService
          .patchCompany({ id: applicant.company.id, body: operations })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(company => {


            applicant.customerId = company.id;
            applicant.company = company;

            obj.next(true);
            obj.complete();

          });

      }
    });
  }


  private createDialogConfig(): MatDialogConfig {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.hasBackdrop = true;
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.width = '840px';
    return dialogConfig;
  }

  private createApplicantNewForm(): UntypedFormGroup {

    const form = this.formBuilder.group({
      customerType: [, Validators.required],
      ownsDutchRealEstate: [, []],
      address: createAddressFormGroup(this.formBuilder),
    });

    form.get('ownsDutchRealEstate').valueChanges.subscribe(value => {
      if (value) {
        form.addControl('address', createAddressFormGroup(this.formBuilder));
      } else {
        form.removeControl('address');
      }
    });

    return form;
  }

  private createApplicantCompanyForm(name: string, company: Company): UntypedFormGroup {
    const form = this.formBuilder.group({
      name: [name, [Validators.required]],
      chamberOfCommerceNumber: [, [Validators.required]],
      legalForm: [, [Validators.required]],
      address: createAddressFormGroup(this.formBuilder),
      contact: this.formBuilder.group({
        id: [, []],
        name: [, []]
      })
    });

    if (company) {
      form.patchValue(company);
    }

    form.markAllAsTouched();

    return form;
  }

  private createShareholderFormGroup(name: string, person: Person): UntypedFormGroup {
    return this.createPersonOrShareholderFormGroup(true, name, person);
  }
  private createPersonFormGroup(name: string, person: Person): UntypedFormGroup {
    return this.createPersonOrShareholderFormGroup(false, name, person);
  }

  private createPersonOrShareholderFormGroup(asShareholder: boolean, name: string, person: Person): UntypedFormGroup {

    let firstName = null;
    let middleName = null;
    let surname = name;

    if (name && name.indexOf(' ') > -1) {
      const parts = name.split(' ');
      firstName = parts[0];

      if (parts.length > 2) {
        middleName = parts.slice(1, parts.length - 1).join(' ');
      }

      surname = parts[parts.length - 1];
    }

    const form = this.formBuilder.group({
      gender: [, [Validators.required]],
      initials: [, [Validators.required]],
      firstName: [firstName, [Validators.required]],
      middleName: [middleName, []],
      surname: [surname, [Validators.required]],
      birthdate: [, [Validators.required]],
      email: [, [Validators.required, Validators.email]],
      phone: [, [Validators.required]],
      nationality: [, [Validators.required]],
      maritalStatus: [, [Validators.required]],
      taxPartner: this.formBuilder.group({
        id: [, []],
        name: [, []]
      }),
      address: createAddressFormGroup(this.formBuilder)
    });

    if (!asShareholder) {
      form.addControl('investmentProfile', this.formBuilder.group({
        actInTheExerciseOfProfessionOrBusiness: [true, [Validators.required]],
        numberOfOwnedInvestmentProperties: [, [Validators.required]],
        yearsOfProfessionalExperienceInRealEstate: [, [Validators.required]],
        netRentalIncomeFromInvestmentPropertiesPerYear: [, [Validators.required]]
      }));
    }

    // Require tax partner for certain marital status
    // form.get('maritalStatus').valueChanges.subscribe(status => {
    //   let taxPartnerControl = form.get('taxPartner.name');
    //   if(status && ) {
    //     taxPartnerControl.setValidators([Validators.required]);
    //   } else {
    //     taxPartnerControl.removeValidators([Validators.required]);
    //   }

    //   taxPartnerControl.updateValueAndValidity();
    //   taxPartnerControl.markAsTouched();
    // });

    if (person) {
      form.patchValue(person);
    }

    form.markAllAsTouched();

    return form;
  }

  private addApplicantShareholderView(applicant: CompanyApplicantView, customer: Customer, shareholder: Shareholder): void {
    const shareholderView = new ShareholderView();
    shareholderView.name = `UBO ${applicant.name}`;
    shareholderView.shareholderId = shareholder?.id;
    shareholderView.search = this.formBuilder.group({ customer: [customer, []] });
    shareholderView.isTaxPartnerApplicantControl = this.createIsTaxPartnerApplicantFormControl(shareholderView);

    if (customer) {
      const isContact = applicant.form.get('contact.id').value === customer.id;
      this.nameShareholder(shareholderView, shareholder, isContact);
    }

    applicant.shareholderViews.push(shareholderView);
  }

  private createIsTaxPartnerApplicantFormControl(view: PersonApplicantView | ShareholderView): FormControl {
    const control = new FormControl(true, []);
    control.valueChanges.subscribe(isTaxPartnerApplicant => {

      const taxPartnerControl = view.form.get('taxPartner.name');
      if (this.hasTaxPartner(view) && !isTaxPartnerApplicant) {
        taxPartnerControl.setValidators([Validators.required]);
      } else {
        taxPartnerControl.removeValidators([Validators.required]);
      }

      taxPartnerControl.updateValueAndValidity();
      taxPartnerControl.markAsTouched();
    });

    return control;
  }
  private doSavePerson(applicant: PersonApplicantView | ShareholderView): Observable<boolean> {

    return new Observable<boolean>((obj) => {

      if (applicant.person == null) {
        const taxPartner = applicant.form.get('taxPartner').value;
        const newPerson = {
          type: 'Person',
          gender: applicant.form.get('gender').value,
          initials: applicant.form.get('initials').value,
          firstName: applicant.form.get('firstName').value,
          middleName: applicant.form.get('middleName').value,
          surname: applicant.form.get('surname').value,
          birthdate: applicant.form.get('birthdate').value,
          address: applicant.form.get('address').value,

          taxPartner: taxPartner.id ? {
            id: taxPartner.id,
            name: taxPartner.name
          } : null,

          nationality: applicant.form.get('nationality').value,
          maritalStatus: applicant.form.get('maritalStatus').value,
          phone: applicant.form.get('phone').value,
          email: applicant.form.get('email').value,

        } as Person;


        if (applicant instanceof PersonApplicantView) {
          newPerson.investmentProfile = {
            actInTheExerciseOfProfessionOrBusiness: applicant.form.get('investmentProfile.actInTheExerciseOfProfessionOrBusiness').value,
          };

          if (this.showExtendedInvestmentProfile(applicant.person)) {
            newPerson.investmentProfile.numberOfOwnedInvestmentProperties = applicant.form.get('investmentProfile.numberOfOwnedInvestmentProperties').value;
            newPerson.investmentProfile.yearsOfProfessionalExperienceInRealEstate = applicant.form.get('investmentProfile.yearsOfProfessionalExperienceInRealEstate').value;
            newPerson.investmentProfile.netRentalIncomeFromInvestmentPropertiesPerYear = applicant.form.get('investmentProfile.netRentalIncomeFromInvestmentPropertiesPerYear').value;
          }
        }

        this.personsService.addPerson({ body: newPerson })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(createdPerson => {

            applicant.customerId = createdPerson.id;
            applicant.person = createdPerson;

            obj.next(true);
            obj.complete();
          });
      } else {
        const operations = convertFormGroupToPatchOperations(applicant.form);

        this.personsService
          .patchPerson({ id: applicant.person.id, body: operations })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(patchedPerson => {
            applicant.person = patchedPerson;
            applicant.customerId = patchedPerson.id;

            obj.next(true);
            obj.complete();
          });
      }
    });
  }

}
